import {
  AiSummaryStatusResponse,
  RecordingStudioStateEnum,
  SlideAssetViewerStateEnum,
  SupportedLanguage,
  LayoutSettings as BaseLayoutSettings,
  type Cue,
  type CueProperties
} from '@goldcast/api/backend';
import { lightTheme } from '@goldcast/utils';

import { Asset } from '../sidebar';
import { EventStageState } from '../stage/types';
import { SurveyState, SurveyStatus } from '../surveys/types';
import { TextQNAState } from '../textQNA/types';

import type { AVRoomState } from '../avroom/types';

import { getValueFromLocalstore } from '@/services/localStorage.service';
import { SpeakerStageRequest } from '@/services/stageControl.service';
import { VideoQNARequest } from '@/services/videoQNA.service';
import { EventHeartbeatState, defaultState } from '@/store/modules/eventHeartbeat/types';
import { RunOfShowItem } from '@/views/event/BackstageControls/Toolbar/RunOfShow/types';

const localTheme = getValueFromLocalstore('themeStyles', {});

const theme = {
  //new variables
  '--stage-text-color': 'var(--primary-text-color)',
  '--stage-background-shade': '255, 255, 255',
  '--stage-background': 'transparent',

  '--theme-primary-color': '#262333', // primary color
  '--primary-text-color': '#fff', // color for text on container with bg color as primary
  '--primary-background-shade': '255, 255, 255', // color of shade on primary color
  '--alt-primary-background-shade': '0, 0, 0', // color of shade on primary color but alternate

  //secondary color variables
  '--theme-secondary-color': '#292f3c', // secondary color
  '--secondary-text-color': '#fff', // color for text directly on container with bg color as seondary color

  '--theme-accent-color': '#2400FF',
  '--accent-text-color': '#fff',

  '--screen-saver-background': 'rgba(255, 255, 255, 1)',
  '--screen-saver-text-color': '#fff',

  '--splash-text-onimage-color': '#fff',
  '--p2p-panel-shade-alpha': 1,
  '--ctrl-bar-shade-alpha': 1,
  '--splash-page-hero': 'transparent',
  '--theme-font-name': '',
  '--theme-font-src': ''
};

export type LayoutSettings = BaseLayoutSettings & {
  showNameCards?: boolean | null;
  show_borders?: boolean | null;
};

export type Widget = {
  /**
   * event widget id
   */
  id: string;
  enabled: boolean;
  tab_title: string;
  /**
   * event specific configuration
   */
  configuration: any;
  event: string;
  widget: {
    /**
     * core widget id
     */
    id: string;
    provider: string;
    title: string;
    js_bundle: any;
    iframe_url: string;
    /**
     * core configuration
     */
    configuration: any;
  };
};

export type NewSlideAnimationAssets = {
  id: string | null;
  current_playing_slide_number: number;
  current_playing_step_number: number;
  slide_state_version: number;
  action?: SlideNavigationAction;
};

export type EventState = {
  ssoprocessing: null | Promise<any>;
  event: {
    agenda: {
      items: AgendaItem[];
      broadcasts: Broadcast[];
    };
    currentLiveAgendaItemIndex: Number;
    tracks: TrackItem[];
    // @soumya, @gaston & @shikhar : Do we need participants to have entire user objects? Or just id's will suffice?
    participants: User[];
    totalParticipants: number;
    resources: any[];
    users: { [k: string]: any };
    invalidateEventNetwork: boolean;
    booths: Booth[];
    dataLoaded: boolean;
    speaker_tab_visible?: boolean;
    rawSpeakersFromUber: BareEventSpeaker[];
    rawOrganizersFromUber: BareEventSpeaker[];
    is_local_recording?: boolean;
    ticketTypes: TicketType[];
    event_type: string;
    video_quality: string;
    speaker_stage_mode?: SpeakerStageMode;
    microphone_control_mode?: MicrophoneControlMode;
  } & any;
  useInHouseSimulive: boolean;
  new_slide_animation_assets: NewSlideAnimationAssets | null;
  // List of discussion groups loaded in the event landing, this is a paginated response
  discussionGroups: DiscussionGroup[];
  discussionGroupUsers: { [k: string]: any };
  eventsList: any[];
  // If the router needs to open a discussion group that is not in the lisf of discussionGroups
  // then it is loaded here
  routeDiscussionGroup?: any;
  avroom?: AVRoomState;
  textQNA?: TextQNAState;
  survey?: SurveyState;
  themeStyles: Object;
  goldcastThemeStyles: any;
  goldcastThemeType: string;
  stage?: EventStageState;
  errorStartingRecordingBroadcastIDs: Record<string, boolean>;
  activePeople: Array<any>;
  organization?: {
    drift_script: string | null;
    goldcast_branding_enabled: boolean;
    id: string;
    name: String;
    shortName: String;
    tier: String | null;
    anonymize_log_rocket: boolean;
    fix_max_video_quality_with_vb: boolean;
    is_event_roles_enabled: boolean;
    enable_hls_captions?: boolean;
    cloudproxy_mode?: Number;
    blacklisted_emojis: string[];
    show_new_entry_experience: boolean;
  };
  fromSplashTo: any;
  widgets: Widget[];
  isVideoQNARequestLoading: boolean;
  stageControl: {
    speakerStageRequests: SpeakerStageRequest[];
    shouldForceMoveToStage: boolean;
    shouldKickFromStage: boolean;
  };
  translator: {
    i18n: any;
    lastUpdated: Date;
  };
  userStateInvalidated: number;
  primaryBooths: Record<string, BoothTiers>;
  secondaryBooths: Record<string, BoothTiers>;
  use_mm_sockets: boolean;
  pricingConfig: Record<string, any>;
  is_new_entry_experience_enabled: boolean;
  edittedStyles: Record<string, any>;
  lastUberFetchEpoch: number;
  eventHeartbeat: EventHeartbeatState;
  supported_languages: SupportedLanguage[];
  events_app_languages: SupportedLanguage[];
  useHlsGuard: boolean;
  useSpeakerActivity: boolean;
  isEventHeartbeatFastEnabled: boolean;
  isNewLoginFlowEnabled: boolean;
  useRecordingPlayPause: boolean;
  isExitingRecording: boolean;
  useEventLocaleForDateTimeFormatting: boolean;
  usePentestFlow: boolean;
  useHideSidebar: boolean;
  use1080VideoQuality: boolean;
  useRegistrantListApi: boolean;
  liveRunOfShow: CueState;
  enableInhouseAddToCalendar: boolean;
  useCloudProxyFlow: boolean;
  useCDNEndpoint: boolean;
  useNewChatWidget: boolean;
  useNewNameCard: boolean;
  disableChatEmoji: boolean;
  useGCAuth: boolean;
  enablePushSpeakerToStage: boolean;
  enableSpeakerReorder: boolean;
  enableRemoveFromBackstage: boolean;
  enableBanAttendeeFromEvent: boolean;
  enableConsentToRaiseHand: boolean;
  useEndEvent: boolean;
  bitrateCap: number;
  useNewROSChanges: boolean;
  useNewSlideAnimation: boolean;
  useFloatingEmoji: boolean;
  useCatchMeUp: boolean;
  catchMeUpSummary: AiSummaryStatusResponse | null;
  catchMeUpGeneratedAt: string | null;
  catchMeUpRequestedTaskId: string | null;
  hasRequestedFullSummary: boolean;
  catchMeUpFeedbackSubmittedForTaskId: string | null;
  hideFloatingEmojiReactions: boolean;
  useNewLayouts: boolean;
  useNewMuteUnmuteCapabilities: boolean;
  cventWebinarIntegration: boolean;
  testingLayoutSettings: {
    noOfSpeakers: number;
    isScreenShareOn: boolean;
  };
  useNewDenoiserV2: boolean;
  isSavingLayout: boolean;
  updatedLayoutSettings: LayoutSettings | null;
  usePlatformBrandKit: boolean;
};

// Cue-related types (using auto-generated API types)

export type LiveRunOfShowCue = Cue;
export type CueProperty = CueProperties;

export interface CueState {
  cues: LiveRunOfShowCue[];
  loading: boolean;
  error: string | null;
  lastFetch: number | null;
  allCuesExpanded: boolean;
  activeCueId: string | null;
}

export type BoothTiers = { name: string; booths: Booth[] };

export type TicketType = {
  id: number;
  allowed_tracks: string[];
  code: string;
  label: string;
  event: string;
};

export type RawBooth = {
  id: string;
  name: string;
  description: string;
  logo: string;
  poster: string;
  sponsor_link: string;
  hero_asset: string;
  hero_asset_type: 'image' | 'video';
  booth_type: 'Primary' | 'Secondary';
  cta: string;
  event: string;
  moderator: string[];
  discussion_group_objects: DiscussionGroup[] | string[];
  tags: string[];
  mmid: string;
  demo_request_title: string;
  demo_request_message: string;
  demo_request_button_text: string;
  demo_request_enabled: boolean;
  tier: string;
  rank: number;
};

export type Booth = RawBooth & {
  english_captions_url?: string;
};

export type BareUser = {
  hidden: Boolean;
  company?: string;
  description?: string;
  display_email: string;
  email: string;
  extra_data?: any;
  id: string;
  is_admin: boolean;
  is_registered: boolean;
  linkedin_url?: string;
  location?: { location: string; place_id: string };
  mmid: string;
  name: string;
  first_name: string;
  last_name: string;
  organization?: string;
  profile_picture_url?: string;
  title?: string;
  twitter_url?: string;
  username: string;
  images?: { [k: string]: string };
  event_role?: number;
};

export type BareEventSpeaker = {
  rank?: number;
  id: string;
  hidden: Boolean;
};

export type User = BareUser & {
  eventUser: EventUser;
  tags: string[];
  image?: string;
  mediumimage?: string;
  thumbnail?: string;
  event: string;
  ticket_codes?: string[];
  track?: string;
  status?: UserStatus;
};

export type UserStatus = {
  agendaFavorites?: AgendaFavorite[];
  isOrganizer: boolean;
  name: String;
  registeredToEvent: boolean;
  blocked: boolean;
};

export type boothActivity = {
  booth: string;
  event_user: string;
  cta_clicked: string;
};

export type EventUser = {
  attended?: string;
  entered?: string;
  booth_activities?: boothActivity[];
  customer_message?: string;
  demo_requested?: boolean;
  event: string;
  event_role?: number;
  quality_rating?: number;
  external_demo_requested?: boolean;
  view_as_attendee: boolean;
  id: string;
  identification_tag?: any;
  kind?: any[];
  props?: any;
  salesperson_email?: string;
  user: BareUser;
  tags?: string[];
  track?: string;
  ticket_codes?: string[];
  blocked: boolean;
  is_in_person?: Boolean;
  event_hub_link?: string;
};

export type AgendaItemType = 'BROADCAST' | 'BREAK' | 'DISCUSSION_GROUP' | 'BOOTH' | 'BOOTH2';

export type SpeakerStageMode = 'INVITE_SPEAKER' | 'PUSH_SPEAKER';

export type MicrophoneControlMode = 'ASK_TO_UNMUTE' | 'DIRECT_UNMUTE';

export const SpeakerStageModeEnum = {
  INVITE_SPEAKER: 'INVITE_SPEAKER',
  PUSH_SPEAKER: 'PUSH_SPEAKER'
} as const;

export const MicrophoneControlModeEnum = {
  ASK_TO_UNMUTE: 'ASK_TO_UNMUTE',
  DIRECT_UNMUTE: 'DIRECT_UNMUTE'
} as const;

export type AgendaItem = {
  allowed_end_time: string;
  id: string;
  have_video_qna?: Boolean;
  start_time: string;
  live_start_time: string;
  live_end_time: string;
  end_time: string;
  title: string;
  event: string;
  is_live: boolean;
  kind?: string;
  type: AgendaItemType;
  sort_priority: number;
  disabled: boolean;
  tracks: string[];
  tickets?: string[];
  is_available_on_demand?: boolean;
  agenda_control_loading_state?: {
    start_button: boolean;
    end_button: boolean;
  };
};

export type AgendaFavorite = {
  agenda_item: any;
  broadcast: string | null;
  event_user: number;
  id: number;
};

export type TrackItem = {
  id: string;
  agenda_items: string[];
  custom: boolean;
  title: string;
  label: string;
  event: string;
  created_at: string;
  updated_at: string;
  channel_mmid: string;
  tickets: TicketType[];
  disabled: boolean;
  sort_priority: number;
};

export type BroadcastSurvey = {
  survey_id: String;
  survey_status: SurveyStatus;
};

export type Broadcast = AgendaItem & {
  audience: string[];
  created_at: string;
  description?: string;
  external_stream_url?: string;
  have_polls: boolean;
  have_text_qna: boolean;
  have_video_qna: boolean;
  id: string;
  is_joinable: boolean;
  is_on_demand: boolean;
  live_end_time?: string;
  expected_end_time?: string;
  live_start_time?: string;
  mmid: string;
  moderator: string[];
  poll_channel_mmid: string;
  speaker_chat_mmid: string;
  recording?: Recording;
  simulive_link?: string;
  speakers: BareUser[];
  speakerIds: string[];
  tags?: string[];
  text_qna_channel_mmid: string;
  title: string;
  updated_at?: string;
  video_qna_requests: VideoQNARequest[];
  is_hybrid_playing_simulive?: boolean;
  simulive_videos?: any;
  broadcast_type?: string;
  slide_assets?: Asset[];
  slide_scroll_position?: number;
  video_assets?: Asset[];
  surveys?: BroadcastSurvey[];
  show_overlay: Boolean;
  show_ticker: Boolean;
  overlay_text: String;
  ticker_text: String;
  ticker_url: String;
  current_playing_slide_number: number | null;
  current_playing_slide_asset: string | null;
  current_playing_video_asset?: string;
  medialive_channel_state?: string;
  is_recording?: boolean;
  medialive_rtmp_input_details?: null | MedialiveRTMPInput;
  medialive_rtmp_input?: null | string;
  lower_third_spec?: null | object;
  stage_logo_spec?: null | object;
  live_captions_mmid: string;
  playback_start_time?: string;
  hide_watcher_count?: boolean;
  layout_settings?: LayoutSettings;
  screen_share_layout?: 'FLEX' | 'FLOATING' | 'NO_SPEAKER_VIDEOS';
  durationOfAllVideoAssets: number;
  expectedSimuliveVideoEndTime: Date;
  expectedSimuliveTestRunVideoEndTime: Date;
  expectedSimuliveTestRunVideoStartTime: Date;
  is_available_on_demand?: boolean;
  on_demand_asset?: OnDemandVideoAsset;
  simulive_test_run_end_time: string;
  slide_asset_viewer_state: SlideAssetViewerStateEnum;
  run_of_show_config: RunOfShowItem[];
  is_automated_run_of_show: boolean;
  recording_studio_state: RecordingStudioStateEnum;
  is_animations_enabled?: boolean;
  animated_slides_url?: string;
  animated_slides_count?: number;
  current_playing_step_number?: number;
  slide_state_version?: number;
  action?: SlideNavigationAction;
};

export type SlideNavigationAction = 'next' | 'prev' | 'go_to_nth' | 'start' | 'stop';

export type OnDemandVideoAsset = {
  hls_path: string;
  fallback_hls_path: string;
  client_hls_path: string;
  urls: {
    hls: string;
  };
};

export type Recording = {
  directory: string;
  resourceId: string;
  sid: string;
};

export type MedialiveRTMPInput = {
  id: string;
  created_at: string;
  updated_at: string;
  name: string;
  channel_states: string;
  channel_id: string;
  input_url: String;
  failover_input_url: String;
  output_url: string;
  event: string;
};

export type DiscussionGroup = {
  capacity: number;
  created_at: string;
  description?: string;
  event: string;
  id: string;
  mmid: string;
  moderator: string[];
  participants?: string[];
  recording?: Recording;
  sponsor_image?: string;
  tags?: string[];
  title: string;
  updated_at: string;
  owner: string;
};

export function getDefaultState(): EventState {
  return {
    ssoprocessing: null,
    event: {
      agenda: {
        broadcasts: [],
        items: []
      },
      currentLiveAgendaItemIndex: 0,
      participants: [],
      totalParticipants: 0,
      resources: [],
      users: {},
      staleEventNetwork: false,
      booths: [],
      tracks: [],
      dataLoaded: false,
      rawSpeakersFromUber: [],
      rawOrganizersFromUber: [],
      allEventSpeakers: [],
      activeUserCount: {},
      activeUserCountUsingMmid: {},
      video_quality: 'REGULAR'
    },
    useInHouseSimulive: false,
    new_slide_animation_assets: null,
    userStateInvalidated: 0,
    // List of discussion groups loaded in the event landing, this is a paginated response
    themeStyles: Object.entries(localTheme).length ? localTheme : theme,
    goldcastThemeStyles: lightTheme,
    goldcastThemeType: 'light',
    discussionGroups: [],
    discussionGroupUsers: {},
    eventsList: [],
    // If the router needs to open a discussion group that is not in the lisf of discussionGroups
    // then it is loaded here
    routeDiscussionGroup: null,
    errorStartingRecordingBroadcastIDs: {},
    activePeople: [],
    organization: undefined,
    fromSplashTo: null,
    widgets: [],
    isVideoQNARequestLoading: false,
    stageControl: {
      speakerStageRequests: [],
      shouldForceMoveToStage: false,
      shouldKickFromStage: false
    },
    translator: {
      i18n: window.i18n ?? { global: { t: (k: string) => k } },
      lastUpdated: new Date()
    },
    primaryBooths: {},
    secondaryBooths: {},
    use_mm_sockets: false,
    pricingConfig: {},
    is_new_entry_experience_enabled: false,
    edittedStyles: {},
    lastUberFetchEpoch: -1,
    eventHeartbeat: defaultState,
    supported_languages: [],
    events_app_languages: [],
    useHlsGuard: false,
    useSpeakerActivity: false,
    isEventHeartbeatFastEnabled: false,
    isNewLoginFlowEnabled: false,
    useRecordingPlayPause: false,
    isExitingRecording: false,
    useEventLocaleForDateTimeFormatting: false,
    usePentestFlow: false,
    useHideSidebar: false,
    use1080VideoQuality: false,
    useRegistrantListApi: false,
    liveRunOfShow: {
      cues: [],
      loading: false,
      error: null,
      lastFetch: null,
      allCuesExpanded: true,
      activeCueId: null
    },
    enableInhouseAddToCalendar: false,
    useCloudProxyFlow: false,
    useCDNEndpoint: false,
    useNewChatWidget: false,
    useNewNameCard: false,
    disableChatEmoji: false,
    enablePushSpeakerToStage: false,
    useGCAuth: false,
    enableSpeakerReorder: false,
    enableRemoveFromBackstage: false,
    enableBanAttendeeFromEvent: false,
    enableConsentToRaiseHand: false,
    useEndEvent: false,
    bitrateCap: 0,
    useNewROSChanges: false,
    useNewSlideAnimation: false,
    useFloatingEmoji: false,
    useCatchMeUp: false,
    catchMeUpSummary: null,
    catchMeUpGeneratedAt: null,
    catchMeUpRequestedTaskId: null,
    hasRequestedFullSummary: false,
    catchMeUpFeedbackSubmittedForTaskId: null,
    hideFloatingEmojiReactions: false,
    useNewLayouts: false,
    useNewMuteUnmuteCapabilities: false,
    cventWebinarIntegration: false,
    testingLayoutSettings: {
      noOfSpeakers: 0,
      isScreenShareOn: false
    },
    useNewDenoiserV2: false,
    isSavingLayout: false,
    updatedLayoutSettings: {},
    usePlatformBrandKit: false
  };
}

export type SettingType =
  | 'color'
  | 'number'
  | 'text'
  | 'padding'
  | 'image'
  | 'boolean'
  | 'select'
  | 'range'
  | 'gradient';

export interface PaddingValue {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
}

export interface SelectOption {
  label: string;
  value: string | number;
}

export interface BaseSettingField {
  id: string;
  label: string;
  type: SettingType;
  description?: string;
  disabled?: boolean;
  required?: boolean;
}

export interface ColorField extends BaseSettingField {
  type: 'color' | 'gradient';
  value: string;
  defaultValue?: string;
  showAlpha?: boolean;
}

export interface NumberField extends BaseSettingField {
  type: 'number';
  value: number;
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: number;
}

export interface TextField extends BaseSettingField {
  type: 'text';
  value: string;
  placeholder?: string;
  defaultValue?: string;
}

export interface PaddingField extends BaseSettingField {
  type: 'padding';
  value: PaddingValue;
  min?: number;
  max?: number;
  step?: number;
  defaultValue?: PaddingValue;
  sides?: Array<'top' | 'right' | 'bottom' | 'left'>;
}

export interface ImageField extends BaseSettingField {
  type: 'image';
  value: string;
  accept?: string;
  maxSize?: number; // in bytes
  defaultValue?: string;
}

export interface BooleanField extends BaseSettingField {
  type: 'boolean';
  value: boolean;
  defaultValue?: boolean;
}

export interface SelectField extends BaseSettingField {
  type: 'select';
  value: string | number;
  options: SelectOption[];
  defaultValue?: string | number;
  multiple?: boolean;
}

export interface RangeField extends BaseSettingField {
  type: 'range';
  value: number;
  min: number;
  max: number;
  step?: number;
  defaultValue?: number;
  showValue?: boolean;
}

export type SettingField =
  | ColorField
  | NumberField
  | TextField
  | PaddingField
  | ImageField
  | BooleanField
  | SelectField
  | RangeField;

export interface SettingBlock {
  id: string;
  title: string;
  description?: string;
  collapsed?: boolean;
  disabled?: boolean;
  fields: SettingField[];
}

export interface SettingsPanelProps {
  blocks: SettingBlock[];
  modelValue: Record<string, any>;
}

// Helper type for extracting field value based on type
export type FieldValue<T extends SettingField> = T['value'];

// Helper functions for type checking
export const isColorField = (field: SettingField): field is ColorField => field.type === 'color';
export const isNumberField = (field: SettingField): field is NumberField => field.type === 'number';
export const isTextField = (field: SettingField): field is TextField => field.type === 'text';
export const isPaddingField = (field: SettingField): field is PaddingField => field.type === 'padding';
export const isImageField = (field: SettingField): field is ImageField => field.type === 'image';
export const isBooleanField = (field: SettingField): field is BooleanField => field.type === 'boolean';
export const isSelectField = (field: SettingField): field is SelectField => field.type === 'select';
export const isRangeField = (field: SettingField): field is RangeField => field.type === 'range';
